import "styles/layout/side-navigation.less";
import { Dispatch, ReactNode, useMemo } from "react";

import { ClockCircleOutlined, LockOutlined } from "@ant-design/icons";
import { Menu, MenuProps } from "antd";
import checkmark from "assets/images/new-questionnaire/checkmark.svg";
import undoneStepBankAccount from "assets/images/new-questionnaire/navigation/undone-step-bank-account.svg";
import undoneStepCapitalGains from "assets/images/new-questionnaire/navigation/undone-step-capital-gains.svg";
import undoneStepCIS from "assets/images/new-questionnaire/navigation/undone-step-cis.svg";
import undoneStepDividends from "assets/images/new-questionnaire/navigation/undone-step-dividends.svg";
import undoneStepEmployment from "assets/images/new-questionnaire/navigation/undone-step-employment.svg";
import undoneStepGeneral from "assets/images/new-questionnaire/navigation/undone-step-general.svg";
import undoneStepInterestIncome from "assets/images/new-questionnaire/navigation/undone-step-interest-income.svg";
import undoneStepPensionIncome from "assets/images/new-questionnaire/navigation/undone-step-pension-income.svg";
import undoneStepPersonalInfo from "assets/images/new-questionnaire/navigation/undone-step-personal-info.svg";
import undoneStepRental from "assets/images/new-questionnaire/navigation/undone-step-rental.svg";
import undoneStepReviewSubmit from "assets/images/new-questionnaire/navigation/undone-step-review-submit.svg";
import undoneStepSelfEmployement from "assets/images/new-questionnaire/navigation/undone-step-self-employment.svg";
import undoneStepStateBenefits from "assets/images/new-questionnaire/navigation/undone-step-state-benefits.svg";
import { useDispatch } from "react-redux";
import { AnyAction } from "redux";
import { useAppSelector } from "redux/hooks";
import { markCategoryInProgress, next } from "redux/reducers/navigation";
import { setCurrentProfile, setCurrentProfileAnswers, setIsEditingProfile } from "redux/reducers/taxProfile";
import { RootState } from "redux/store";
import { useLazyGetClientTaxProfileQuery } from "services/client";
import { ArrayAnswersType } from "types/questionnaire";
import { scrollToTop } from "utils/common";

import { QuestionID } from "../../constants/questionIds";
import { categories } from "../../constants/questions";
import { CategoryTitle } from "../../enums/enum";

/* eslint-disable */
export enum StepStatus {
    undone = "undone",
    active = "active",
    done = "done",
    disallowed = "disallowed",
    inProgress = "inProgress",
}
/* eslint-enable */

export const getStatusIcon = (status: StepStatus) => {
    switch (status) {
    case StepStatus.done:
        return <img src={checkmark} alt="done" className="dblock ml3"/>;
    case StepStatus.inProgress:
        return <ClockCircleOutlined className="ml3"/>;
    case StepStatus.disallowed:
        return <LockOutlined className="ml3"/>;
    default:
        return null;
    }
};

interface SideNavigationProps {
    close?: () => void;
}
const sectionNameQuestionIds: Record<string, string> = {
    [CategoryTitle.SelfEmployment]: "SELF_EMPLOYMENT_NAME",
    [CategoryTitle.Employment]: "EMPLOYMENT_NAME",
    [CategoryTitle.Rental]: "RENTAL_NAME",
    [CategoryTitle.CapitalGains]: "CAPITAL_GAIN_LOSS_NAME",
    [CategoryTitle.Dividends]: "DIVIDENDS_NAME",
    [CategoryTitle.InterestIncome]: "INTEREST_NAME",
};

const SideNavigation = ({ close }: SideNavigationProps) => {
    const dispatch = useDispatch();
    const user = useAppSelector((state: RootState) => state.auth.user);
    const { navigationItems, currentCategory, completedCategories, inProgressCategories } = useAppSelector((state: RootState) => state.navigation);
    const { answers } = useAppSelector((state: RootState) => state.answers);

    const [getClientTaxProfile] = useLazyGetClientTaxProfileQuery();

    const handleFetchClientTaxProfile = async (taxProfileId: string) => {
        const response = await getClientTaxProfile({
            client: user?.id!,
            taxProfileId: taxProfileId,
        }).unwrap();
        if (!response) {
            return undefined;
        }

        return response.taxProfile; // Return the fetched profile
    };

    const isProfileLandingPage = (taxProfileId: QuestionID) => {
        return (
            taxProfileId === "SELF_EMPLOYMENT_PROFILES" ||
            taxProfileId === "CIS_EMPLOYMENT_PROFILES" ||
            taxProfileId === "EMPLOYMENT_PROFILES" ||
            taxProfileId === "RENTAL_PROFILES" ||
            taxProfileId === "DIVIDENDS_PROFILES" ||
            taxProfileId === "CAPITAL_GAIN_LOSS_PROFILES" ||
            taxProfileId === "INTEREST_PROFILES"
        );
    };

    const isSectionDisabled = (
        categoryTitle: string,
        questionId: QuestionID | string,
        itemId?: string // optional, only used for profiles
    ) => {
        const categoryAnswers = answers[categoryTitle];
        if (!categoryAnswers) return true;

        const scopedAnswers: any[] = [];

        // If checking a profile-type section (like CIS or Self Employment), filter by itemId
        if (itemId) {
            const profileKey = Object.keys(categoryAnswers).find(key => Array.isArray(categoryAnswers[key]));
            if (!profileKey) return true;

            const profileList = categoryAnswers[profileKey] as ArrayAnswersType[];

            const matchingProfile = profileList.find(profile => profile.itemId === itemId);
            if (!matchingProfile) return true;

            const flatten = (value: any) => {
                if (Array.isArray(value)) {
                    value.forEach(item => flatten(item));
                } else if (value && typeof value === "object") {
                    if ("id" in value) scopedAnswers.push(value);
                    Object.values(value).forEach(v => flatten(v));
                }
            };

            flatten(matchingProfile.answers);
        } else {
            scopedAnswers.push({ question: questionId, id: questionId, answer: categoryAnswers[questionId] });
        }
        return !scopedAnswers.some(answer => answer?.id === questionId);
    };

    /** Clears editing/profile state */
    const resetProfileState = () => {
        dispatch(setIsEditingProfile(false));
        dispatch(setCurrentProfile(undefined));
        dispatch(setCurrentProfileAnswers([]));
    };

    const getProfileQuestionFromId = async (
        category: number,
        step: number,
        e: any
    ): Promise<number> => {
        const taxProfileId = e.keyPath?.[e.keyPath.length - 2];
        if (!taxProfileId || isProfileLandingPage(taxProfileId)) {
            resetProfileState();
            return 0;
        }

        const currentProfile = await handleFetchClientTaxProfile(taxProfileId);
        if (!currentProfile) {
            resetProfileState();
            return 0;
        }

        const categoryTitle = categories[category]?.title;
        const categoryAnswersObj = answers[categoryTitle] as Record<string, any>;
        const profiles = Object.values(categoryAnswersObj ?? {})[0] as any[]; // first value is array
        const profileEntry = profiles?.find(p => p.itemId === taxProfileId);

        if (profileEntry?.answers?.length) {
            dispatch(setCurrentProfile(currentProfile));
            dispatch(setCurrentProfileAnswers(profileEntry.answers));
            dispatch(setIsEditingProfile(true));
        } else {
            resetProfileState();
        }

        const questions =
        categories[category]?.steps?.[step]?.questions?.[0].questions ?? [];

        const qIndex = questions.findIndex(q => q.id === e.key);

        const cIndex = questions.findIndex(q =>
            Array.isArray(q.cards) && q.cards.some(c => c.id === e.key)
        );

        if (qIndex !== -1) return qIndex;
        if (cIndex !== -1) return cIndex;

        return 0;
    };

    const getStepFromId = (category: number, e: any) => {
        dispatch(setIsEditingProfile(false));
        dispatch(setCurrentProfile(undefined));
        dispatch(setCurrentProfileAnswers([]));
        if (category === 0) {
            return 1;
        }

        const step = categories[category]?.steps.findIndex(step =>
            (step.questions?.some(q => q.id === e.key) ?? false) ||
            (step.cards?.some(c => c.id === e.key) ?? false)
        );

        return step !== -1 ? step : 0;
    };

    const onClick: MenuProps["onClick"] = async (e: any) => {
        scrollToTop();
        const category = Number(e.keyPath[e.keyPath.length - 1]);
        const step = getStepFromId(category, e);
        let profileQuestion = 0;
        if (categories[category]?.steps[step]?.parentProfileType) {
            profileQuestion = await getProfileQuestionFromId(category, step, e);
        }
        dispatch(next({ nextCategory: category, nextStep: step, nextQuestion: 0, nextProfileQuestion: profileQuestion }));
        close && close();
    };

    const menuItems = [
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.BankAccountConnection),
            label: CategoryTitle.BankAccountConnection,
            type: undefined,
            icon: <img src={undoneStepBankAccount} alt="bank account" height={32}/>,
            disabled: false,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.PersonalInfo),
            label: CategoryTitle.PersonalInfo,
            type: undefined,
            icon: <img src={undoneStepPersonalInfo} alt="personal info" height={32} />,
            disabled: false,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.SelfEmployment),
            label: CategoryTitle.SelfEmployment,
            type: undefined,
            icon: <img src={undoneStepSelfEmployement} alt="self employment" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.CIS),
            label: CategoryTitle.CIS,
            type: undefined,
            icon: <img src={undoneStepCIS} alt="cis" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.Employment),
            label: CategoryTitle.Employment,
            type: undefined,
            icon: <img src={undoneStepEmployment} alt="employment" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.Rental),
            label: CategoryTitle.Rental,
            type: undefined,
            icon: <img src={undoneStepRental} alt="rental" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.General),
            label: CategoryTitle.General,
            type: undefined,
            icon: <img src={undoneStepGeneral} alt="general" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.PensionIncome),
            label: CategoryTitle.PensionIncome,
            type: undefined,
            icon: <img src={undoneStepPensionIncome} alt="pension income" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.StateBenefits),
            label: CategoryTitle.StateBenefits,
            type: undefined,
            icon: <img src={undoneStepStateBenefits} alt="state benefits" height={32} style={{ opacity: 1 }} />,
            disabled: true,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.CapitalGains),
            label: CategoryTitle.CapitalGains,
            type: undefined,
            icon: <img src={undoneStepCapitalGains} alt="capital gains" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.Dividends),
            label: CategoryTitle.Dividends,
            type: undefined,
            icon: <img src={undoneStepDividends} alt="dividends" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.InterestIncome),
            label: CategoryTitle.InterestIncome,
            type: undefined,
            icon: <img src={undoneStepInterestIncome} alt="interest income" height={32} style={{ opacity: 1 }} />,
            disabled: true,
            children: null,
        },
        {
            key: categories?.findIndex(category => category?.title === CategoryTitle.ReviewAndSubmit),
            label: CategoryTitle.ReviewAndSubmit,
            type: undefined,
            icon: <img src={undoneStepReviewSubmit} alt="review and submit" height={32} style={{ opacity: 1 }} />,
            disabled: true,
        },

    ];

    const generateChildrenForCategory = (label: CategoryTitle, isParentDisabled: boolean): any => {
        const getProfiles = (
            sectionKey: CategoryTitle,
            incomeId: QuestionID,
            expensesId?: QuestionID
        ) => {
            const section = answers[sectionKey];
            if (!section || Object.keys(section).length === 0) return null;

            const profileKey = Object.keys(section).find(key => Array.isArray(section[key]));
            if (!profileKey) return null;
            return (section[profileKey] as ArrayAnswersType[]).map((profile: ArrayAnswersType) => {
                const children = [
                    {
                        key: profile.answers[0]?.id,
                        label: <span className="ml6">General Questions</span>,
                        type: undefined,
                        icon: null,
                        disabled: isParentDisabled,
                        children: null,
                    },
                    {
                        key: incomeId,
                        label: <span className="ml6">Income</span>,
                        type: undefined,
                        icon: null,
                        disabled: isSectionDisabled(sectionKey, incomeId, profile.itemId) || isParentDisabled,
                        children: null,
                    },
                ];

                if (expensesId) {
                    children.push({
                        key: expensesId,
                        label: <span className="ml6">Expenses / Benefits</span>,
                        type: undefined,
                        icon: null,
                        disabled: isSectionDisabled(sectionKey, expensesId, profile.itemId) || isParentDisabled,
                        children: null,
                    });
                }

                return {
                    key: profile.itemId,
                    label: <span className="ml20">{sectionKey === CategoryTitle.CIS ? "CIS Employment" : profile.answers.find((ans: any) => ans.id === sectionNameQuestionIds[sectionKey])?.answer || profile.item}</span>,
                    type: undefined,
                    icon: null,
                    disabled: isParentDisabled,
                    children,
                };
            });
        };

        switch (label) {
        case CategoryTitle.PersonalInfo:
            return [
                {
                    key: "PERSONAL_INFO_JOB_STATUS",
                    label: <span className="ml20">Primary Income</span>,
                    type: undefined,
                    icon: null,
                    disabled: isSectionDisabled(CategoryTitle.PersonalInfo, "Select your primary income sources"),
                    children: null,
                },
                {
                    key: "PERSONAL_INFO_ADDITIONAL_INCOME",
                    label: <span className="ml20">Additional Income</span>,
                    type: undefined,
                    icon: null,
                    disabled: isSectionDisabled(CategoryTitle.PersonalInfo, "Select your primary income sources"),
                    children: null,
                },
            ];
        case CategoryTitle.SelfEmployment:
        {
            const profiles = getProfiles(
                CategoryTitle.SelfEmployment,
                "SELF_EMPLOYMENT_INCOME_SUMMARY",
                "SELF_EMPLOYMENT_EXPENSES_SUMMARY"
            ) ?? [];

            return [
                {
                    key: "SELF_EMPLOYMENT_PROFILES",
                    label: <span className="ml20">Add Self Employment</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }

        case CategoryTitle.CIS:
        {
            const profiles = getProfiles(
                CategoryTitle.CIS,
                "CIS_EMPLOYMENT_INCOME_SUMMARY",
                "CIS_EMPLOYMENT_EXPENSES_SUMMARY"
            ) ?? [];
            return [
                {
                    key: "CIS_EMPLOYMENT_PROFILES",
                    label: <span className="ml20">Add CIS Employment</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }
        case CategoryTitle.Employment:
        {
            const profiles = getProfiles(
                CategoryTitle.Employment,
                "EMPLOYMENT_INCOME_SUMMARY",
                "EMPLOYMENT_BENEFITS_CARD"
            ) ?? [];
            return [
                {
                    key: "EMPLOYMENT_PROFILES",
                    label: <span className="ml20">Add PAYE Employment</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }
        case CategoryTitle.Rental:
        {
            const rentalProfiles = getProfiles(
                CategoryTitle.Rental,
                "RENTAL_MANUAL_INCOME",
                "RENTAL_EXPENSES_SUMMARY"
            ) ?? [];
            return [
                {
                    key: "RENTAL_PROFILES",
                    label: <span className="ml20">Add Rental</span>,
                    disabled: isParentDisabled,
                },
                ...rentalProfiles,
            ];
        }
        case CategoryTitle.Dividends:{
            const profiles = getProfiles(CategoryTitle.Dividends, "DIVIDENDS_AMOUNT") ?? [];
            return [
                {
                    key: "DIVIDENDS_PROFILES",
                    label: <span className="ml20">Add Dividend</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }
        case CategoryTitle.CapitalGains:
        {
            const profiles = getProfiles(CategoryTitle.CapitalGains, "CAPITAL_GAIN_LOSS_ASSET_VALUES") ?? [];
            return [
                {
                    key: "CAPITAL_GAIN_LOSS_PROFILES",
                    label: <span className="ml20">Add Capital Gain</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }
        case CategoryTitle.InterestIncome:
        {
            const profiles = getProfiles(CategoryTitle.InterestIncome, "INTEREST_AMOUNT") ?? [];
            return [
                {
                    key: "INTEREST_PROFILES",
                    label: <span className="ml20">Add Interest Income</span>,
                    disabled: isParentDisabled,
                },
                ...profiles,
            ];
        }
        case CategoryTitle.General:
            return [
                {
                    key: "GENERAL_OPTIONS_APPLY",
                    label: <span className="ml20">Extra Info</span>,
                    type: undefined,
                    icon: null,
                    disabled: isParentDisabled,
                },
                {
                    key: "GENERAL_STUDENT_LOAN",
                    label: <span className="ml20">Student Loan</span>,
                    type: undefined,
                    icon: null,
                    disabled: isSectionDisabled(CategoryTitle.General, "GENERAL_STUDENT_LOAN") || isParentDisabled,
                },
                {
                    key: "GENERAL_PAYMENTS_ON_ACCOUNT",
                    label: <span className="ml20">Payments on Account</span>,
                    type: undefined,
                    icon: null,
                    disabled: isSectionDisabled(CategoryTitle.General, "GENERAL_PAYMENTS_ON_ACCOUNT") || isParentDisabled,
                },
            ];
        case CategoryTitle.PensionIncome:
            return [
                {
                    key: "PENSION_INCOME_OPTIONS_APPLY",
                    label: <span className="ml20">Pension Income Options</span>,
                    type: undefined,
                    icon: null,
                    disabled: isParentDisabled,
                },
            ];
        case CategoryTitle.StateBenefits:
            return [
                {
                    key: "STATE_BENEFITS_OPTIONS_APPLY",
                    label: <span className="ml20">State Benefits Options</span>,
                    type: undefined,
                    icon: null,
                    disabled: isParentDisabled,
                },
            ];
        default:
            return undefined;
        }
    };

    const getRelevantNavigationItems = (
        navTabs: string[],
        currentCategory: number,
        completedCategories: CategoryTitle[],
        inProgressCategories: CategoryTitle[],
        dispatch: Dispatch<AnyAction>
    ) => {
    // Sort and filter categories based on provided navTabs
        const navigationItems = menuItems
            .filter(item => item && navTabs.includes(item.label))
            .sort((a, b) => (Number(a.key) || 0) - (Number(b.key) || 0));

        // Find the earliest in-progress category index
        const earliestInProgressIndex = navigationItems.findIndex(item =>
            inProgressCategories.includes(item.label as CategoryTitle)
        );

        navigationItems.forEach((item, index) => {
            const isActiveCategory =
                completedCategories.includes(item.label as CategoryTitle) ||
                inProgressCategories.includes(item.label as CategoryTitle) ||
                categories[currentCategory]?.title === item.label;

            // Everything after the earliest in-progress category is disabled
            if (earliestInProgressIndex !== -1 && index > earliestInProgressIndex) {
                item.disabled = true;
            } else {
                item.disabled = false;
            }

            // Mark earlier categories as in-progress
            if (index < earliestInProgressIndex && !isActiveCategory) {
                dispatch(markCategoryInProgress(item.label as CategoryTitle));
            }

            // Adjust icon opacity based on disabled state
            const originalIcon = item.icon as ReactNode;
            item.icon = (
                <span style={{ opacity: item.disabled ? 0.4 : 1 }}>
                    {originalIcon}
                </span>
            );

            // Inject children dynamically here
            const label = item.label as CategoryTitle;

            item.children = generateChildrenForCategory(label, item.disabled);
        });

        return navigationItems;
    };

    const dynamicItems = useMemo(() => {
        return getRelevantNavigationItems(navigationItems, currentCategory, completedCategories, inProgressCategories, dispatch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigationItems, completedCategories, inProgressCategories, currentCategory, dispatch]);

    return (
        <Menu
            className="side-navigation"
            onClick={onClick}
            mode="inline"
            items={dynamicItems}
            style={
                { fontSize: "15px" }
            }
        />
    );
};

export default SideNavigation;
